/**
 * C4-PlantUML emitter — voor view-kinds die starten met `c4.` (c4.context,
 * c4.container, c4.component, c4.deployment, c4.dynamic, c4.system-landscape).
 *
 * Gebruikt Simon Brown's C4-PlantUML stdlib (`<C4/C4_Container>` etc.)
 * i.p.v. ArchiMate-stdlib. Andere procedure-naming: `Person(...)`,
 * `System(...)`, `Container(...)`, `Component(...)` voor elements; en
 * `Rel(from, to, label, technology)` plus `Rel_U/D/L/R` voor relations.
 *
 * Element-mapping: per concept lookt 'ie naar z'n stereotypes om de
 * juiste C4-procedure te kiezen. Een ApplicationComponent met
 * `c4.Container`-stereotype → `Container(...)`; met `c4.SoftwareSystem`
 * → `System(...)`; zonder herkenbare C4-stereotype → fallback op
 * `Container(...)` als generieke C4-element.
 *
 * Geen roundtrip-parser voor C4-syntax — canvas blijft canonical voor
 * c4-views. Tekst-edit van C4-PlantUML is een latere milestone-feature.
 *
 * Zie docs/architecture/plantuml-aanhechting.md voor de keuze-rationale.
 */
import type { BilingualText, Concept, Relation } from "../types";

export interface C4EmitOptions {
  lang?: "nl" | "en";
  /**
   * View-kind van het actieve diagram (bv. `c4.container`). Bepaalt
   * welke C4-stdlib-include we gebruiken. Default `c4.container`
   * (Container-niveau is het meest gebruikt).
   */
  viewKind?: string;
  title?: string;
  /**
   * Per-relation source-handle (`top|right|bottom|left`). Wordt
   * vertaald naar `Rel_U/_D/_L/_R`-suffix zodat de PlantUML-render
   * de canvas-aanhechtzijde nabootst. Identiek aan
   * `archimate-emitter`'s `handles.source`; target-handle wordt door
   * C4-stdlib niet ondersteund (geen `Rel_From_To_Direction`).
   */
  handles?: Record<string, { source?: string | undefined }>;
  /**
   * PlantUML lijn-routing — identiek aan `EmitOptions.lineType` in
   * archimate-emitter. `"ortho"` (default) / `"polyline"` / `"default"`.
   */
  lineType?: "default" | "polyline" | "ortho";
}

/**
 * C4-stdlib-include per view-kind. Onbekende c4.* view-kinds vallen
 * terug op C4_Container (meest gebruikt, includeert ook Context-
 * procedures). Wordt alleen gebruikt als baseline wanneer er geen
 * concepten zijn die een specifiekere include forceren — zie
 * `pickIncludes` voor de runtime-bepaling.
 */
const INCLUDE_BY_VIEW: Record<string, string> = {
  "c4.context": "<C4/C4_Context>",
  "c4.system-landscape": "<C4/C4_Context>",
  "c4.container": "<C4/C4_Container>",
  "c4.component": "<C4/C4_Component>",
  "c4.deployment": "<C4/C4_Deployment>",
  "c4.dynamic": "<C4/C4_Dynamic>",
};

/**
 * Bepaalt de minimale set van C4-stdlib-includes die nodig is om alle
 * procedure-calls in `usedProcedures` te dekken. Simon Brown's
 * library-hiërarchie:
 *   C4 (base)
 *     ↳ C4_Context     (Person, System*, System_Boundary)
 *         ↳ C4_Container   (+ Container*, ContainerDb, ContainerQueue,
 *                            Container_Boundary)
 *             ↳ C4_Component (+ Component)
 *             ↳ C4_Deployment (+ Deployment_Node, Node)  — sibling van Component
 *         ↳ C4_Dynamic — sibling van Container-chain (+ RelIndex)
 *
 * Component en Deployment zijn ZUSTERS (niet genest), dus bij een
 * diagram dat zowel Component als Deployment_Node bevat moet je
 * beide includes emitten. Default fall-back op `viewKindInclude`
 * dekt het geval "leeg diagram".
 */
function pickIncludes(usedProcedures: Set<string>, viewKindInclude: string): string[] {
  const needsComponent = usedProcedures.has("Component");
  const needsDeployment = usedProcedures.has("Deployment_Node") || usedProcedures.has("Node");
  const containerProcs = new Set([
    "Container",
    "ContainerDb",
    "ContainerQueue",
    "Container_Ext",
    "Container_Boundary",
  ]);
  const contextProcs = new Set(["Person", "System", "System_Ext", "System_Boundary"]);
  const needsContainer = [...usedProcedures].some((p) => containerProcs.has(p));
  const needsContext = [...usedProcedures].some((p) => contextProcs.has(p));

  const includes = new Set<string>();
  if (needsComponent) includes.add("<C4/C4_Component>");
  if (needsDeployment) includes.add("<C4/C4_Deployment>");
  if (!needsComponent && !needsDeployment) {
    if (needsContainer) includes.add("<C4/C4_Container>");
    else if (needsContext) includes.add("<C4/C4_Context>");
  }
  if (includes.size === 0) includes.add(viewKindInclude);
  return [...includes];
}

/**
 * Direction-suffix voor C4-Rel-procedures (Rel_U / Rel_D / Rel_L / Rel_R).
 * NB: C4-naming verschilt van ArchiMate (`_Up` / `_Down` / `_Left` /
 * `_Right`) — alleen de eerste letter.
 */
function c4DirectionSuffix(sourceHandle: string | undefined): string {
  switch (sourceHandle) {
    case "right":
      return "_R";
    case "left":
      return "_L";
    case "top":
      return "_U";
    case "bottom":
      return "_D";
    default:
      return "";
  }
}

/**
 * Per-concept-stereotypes → C4-procedure-naam. Gecheckt in volgorde van
 * meest-specifiek → minst-specifiek. Sub-stereotypes (Database,
 * MessageBroker etc.) winnen van hun parent (`c4.Container`).
 */
function pickC4Procedure(concept: Concept): string {
  const stereos = concept.stereotypes ?? [];
  if (stereos.includes("c4.Database")) return "ContainerDb";
  if (stereos.includes("c4.MessageBroker")) return "ContainerQueue";
  if (stereos.includes("c4.Microservice")) return "Container";
  if (stereos.includes("c4.Container")) return "Container";
  if (stereos.includes("c4.Component")) return "Component";
  if (stereos.includes("c4.Person")) return "Person";
  if (stereos.includes("c4.SoftwareSystem")) return "System";
  if (stereos.includes("c4.ExternalSystem")) return "System_Ext";
  if (stereos.includes("c4.ContainerBoundary")) return "Container_Boundary";
  if (stereos.includes("c4.SystemBoundary")) return "System_Boundary";
  if (stereos.includes("c4.DeploymentNode")) return "Deployment_Node";
  // Fallback op basis van base-type (legacy `c4_level`-attribute van
  // pre-M5-tijd):
  if (concept.typeId === "archimate.BusinessActor") return "Person";
  if (concept.typeId === "archimate.Node") return "Deployment_Node";
  if (concept.typeId === "archimate.ApplicationComponent") {
    const level = (concept.attributes as Record<string, unknown> | undefined)?.c4_level;
    if (level === "system") return "System";
    if (level === "component") return "Component";
    return "Container";
  }
  // Onbekend → generic Container (C4 heeft geen "Element"-fallback).
  return "Container";
}

function resolveBilingual(text: BilingualText, preferred: string): string {
  return (
    text[preferred] ??
    text.nl ??
    text.en ??
    Object.values(text).find((v): v is string => typeof v === "string" && v.length > 0) ??
    ""
  );
}

function sanitizeId(id: string): string {
  return `n_${id.replace(/-/g, "_")}`;
}

function escapeQuotes(s: string): string {
  // PlantUML string-literals kunnen niet over fysieke regels heen — een
  // letterlijke newline in `name` of `description` breekt de parser
  // (syntax error op de verdwaalde tweede regel). Converteer \n/\r naar
  // de PlantUML-line-break-escape `\n` (twee karakters: backslash + n)
  // zodat de tekst als multi-line label binnen het element rendert.
  return s
    .replace(/\\/g, "\\\\")
    .replace(/"/g, '\\"')
    .replace(/\r\n/g, "\\n")
    .replace(/[\r\n]/g, "\\n");
}

export function emitC4PlantUML(
  concepts: readonly Concept[],
  relations: readonly Relation[],
  options: C4EmitOptions = {},
): string {
  const lang = options.lang ?? "nl";
  const viewKind = options.viewKind ?? "c4.container";
  const viewKindInclude = INCLUDE_BY_VIEW[viewKind] ?? "<C4/C4_Container>";

  const visibleConcepts = concepts.filter((c) => !c.tombstoneInBranch);
  const visibleRelations = relations.filter((r) => !r.tombstoneInBranch);

  // Bepaal welke includes echt nodig zijn op basis van de procedures
  // die we straks gaan emitten. Voorkomt unknown-procedure-errors
  // wanneer een c4.container-view bv. ook Deployment_Nodes bevat
  // (Deployment_Node zit in C4_Deployment, niet in C4_Container).
  const usedProcedures = new Set(visibleConcepts.map((c) => pickC4Procedure(c)));
  const includes = pickIncludes(usedProcedures, viewKindInclude);

  const lines: string[] = [];
  lines.push("@startuml");
  for (const inc of includes) {
    lines.push(`!include ${inc}`);
  }
  // Lijn-routing instelbaar via options.lineType, default "ortho" voor
  // consistentie met archimate-emit. `!pragma layout elk` is bewust
  // weggelaten omdat kroki's PlantUML-distributie de ELK-jar niet
  // bundelt (ClassNotFoundException). Zie de comment in
  // archimate-emitter.ts voor de volledige uitleg.
  const lineType = options.lineType ?? "ortho";
  if (lineType === "ortho") lines.push("skinparam linetype ortho");
  else if (lineType === "polyline") lines.push("skinparam linetype polyline");
  // "default" → géén skinparam, PlantUML gebruikt curved/spline routing
  lines.push("");
  if (options.title) {
    lines.push(`title "${escapeQuotes(options.title)}"`);
    lines.push("");
  }

  // Elements
  for (const c of visibleConcepts) {
    const proc = pickC4Procedure(c);
    const id = sanitizeId(c.id);
    const label = escapeQuotes(resolveBilingual(c.name, lang));
    const descr = c.description ? escapeQuotes(resolveBilingual(c.description, lang)) : "";
    // C4-procedures hebben verschillende parameter-aantallen. Vrij
    // simpele heuristiek voor MVP: Container/Component/ContainerDb/
    // ContainerQueue krijgen 4 args (alias, label, technology, descr);
    // Person/System krijgen 3 (alias, label, descr); Boundary's 2
    // (alias, label). Technology is voor MVP altijd leeg — kan later
    // uit een `technology`-attribuut komen.
    if (
      proc === "Container" ||
      proc === "ContainerDb" ||
      proc === "ContainerQueue" ||
      proc === "Container_Ext" ||
      proc === "Component"
    ) {
      lines.push(`${proc}(${id}, "${label}", "", "${descr}")`);
    } else if (proc === "System_Boundary" || proc === "Container_Boundary") {
      lines.push(`${proc}(${id}, "${label}")`);
    } else if (proc === "Deployment_Node") {
      lines.push(`${proc}(${id}, "${label}", "", "${descr}")`);
    } else {
      // Person, System, System_Ext
      lines.push(`${proc}(${id}, "${label}", "${descr}")`);
    }
  }

  if (visibleConcepts.length > 0 && visibleRelations.length > 0) {
    lines.push("");
  }

  // Relations — alle C4-relations via generieke `Rel`-procedure met
  // directionele suffix uit canvas-source-handle. C4-stdlib heeft
  // geen aparte procedures per relatie-type (anders dan ArchiMate);
  // de "betekenis" zit in label + technology + visuele lijn.
  for (const r of visibleRelations) {
    const fromId = sanitizeId(r.sourceConceptId);
    const toId = sanitizeId(r.targetConceptId);
    const label = escapeQuotes(resolveBilingual(r.label, lang));
    // Per ontwerp: C4-relaties hebben technology als 2e tekst-arg na
    // label. Voor MVP: leeg. Later uit `c4.Connection`-stereotype's
    // technology-attribuut halen.
    const techn = "";
    const dirSuffix = c4DirectionSuffix(options.handles?.[r.id]?.source);
    const proc = `Rel${dirSuffix}`;
    lines.push(`${proc}(${fromId}, ${toId}, "${label}", "${techn}")`);
  }

  lines.push("");
  lines.push("@enduml");
  return lines.join("\n");
}
